import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { sameAddress } from "../lib/address";
import { api, ApiError, isAbortError, type MeAnswer } from "../lib/api";
import { markHandoffShown, pendingHandoff, type HandoffOutcome } from "../lib/handoff";
import { NetworkProvider } from "../lib/network";
import { clearSession, endSession, getSession, isExpired, onSessionEnded, touchesStoredSession, type MakerSession, type SessionEnd, type SessionKind } from "../lib/session";
import * as wallet from "../lib/wallet";
import { HandoffScreen } from "./HandoffScreen";
import { SignInScreen, type SignInNotice } from "./SignInScreen";

/**
 * The Maker portal's only gate: until the API reports a session for the stored
 * token, the sign-in card replaces the page in place, so the URL is kept. The
 * session is not the connected wallet account; nothing here listens to the
 * wallet. Sign out also hangs a WalletConnect pairing up: the phone must keep
 * no live session for a browser that signed out. One session per browser:
 * when another tab stores or drops one, this tab checks again, so a page never
 * shows one market maker while its calls act as another session.
 */

type GateState =
  | { kind: "handoff" }
  | { kind: "checking" }
  | { kind: "signed-out"; notice: SignInNotice | null }
  | { kind: "signed-in"; session: MakerSession; me: MeAnswer };

export interface MakerContextValue {
  session: MakerSession;
  /** Who the session is: the company, its executor identity, the networks and AileFX's filler on each. */
  me: MeAnswer;
  /** `operator`: an AileFX operator from the admin console, on the desk, with no wallet; else an operator wallet. */
  kind: SessionKind;
  /** Ends the session; back to the sign-in card. */
  signOut: () => void;
}

const MakerContext = createContext<MakerContextValue | null>(null);

/** The signed-in market maker. Only rendered inside the gate, so never null there. */
export function useMaker(): MakerContextValue {
  const value = useContext(MakerContext);
  if (value === null) throw new Error("useMaker() outside <MakerGate>");
  return value;
}

/** The session's kind; `wallet` outside the gate (a dialog rendered on its own). */
export function useSessionKind(): SessionKind {
  return useContext(MakerContext)?.kind ?? "wallet";
}

/**
 * Whether a change `signer` must send is offered (its field and its Set): to
 * the signer as the connected wallet, and in a session from the admin console,
 * which holds no wallet, to whoever is connected, the review staying read-only
 * until the signer connects. Never while the signer could not be read.
 */
export function maySet(kind: SessionKind, connected: string | null, signer: string | null): boolean {
  if (signer === null) return false;
  return kind === "operator" || sameAddress(connected, signer);
}

export const ENDED: SignInNotice = { text: "Your session ended. Sign in again with an operator wallet.", tone: "grey" };

/** A session from the admin console ended (its 60 minutes, the console session, the pass, the desk mark). */
export const CONSOLE_ENDED: SignInNotice = {
  text: "Your session from the admin console has ended. Open the Maker portal again from the admin console.",
  tone: "grey",
  above: true,
};

function endedNotice(kind: SessionKind): SignInNotice {
  return kind === "operator" ? CONSOLE_ENDED : ENDED;
}

function stateAfter(end: SessionEnd, kind: SessionKind): GateState {
  return { kind: "signed-out", notice: end === "signed-out" ? null : endedNotice(kind) };
}

/** The admin console's code was refused (or never answered); no session is kept. */
function afterHandoff(outcome: HandoffOutcome): GateState {
  return outcome.kind === "signed-in" ? { kind: "checking" } : { kind: "signed-out", notice: { text: outcome.text, tone: "grey", above: true } };
}

function fromStorage(): GateState {
  const session = getSession();
  if (session === null) return { kind: "signed-out", notice: null };
  if (isExpired(session)) {
    clearSession();
    return { kind: "signed-out", notice: endedNotice(session.kind) };
  }
  return { kind: "checking" };
}

function initialState(): GateState {
  // A tab the admin console opened: its code goes first, whatever this browser held.
  return pendingHandoff() !== null ? { kind: "handoff" } : fromStorage();
}

/** setTimeout's ceiling (~24.8 days): never overflow. */
const MAX_TIMEOUT_MS = 2_147_483_647;

export function MakerGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(initialState);

  // A session ended anywhere: a 401 on any call, the expiry timer, Sign out.
  useEffect(() => onSessionEnded((end, kind) => setState(stateAfter(end, kind))), []);

  // Another tab stored another session or dropped it: this tab's calls already carry what is
  // stored, so it asks again who that is (a new "checking"), or shows the sign-in card.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!touchesStoredSession(event)) return;
      const stored = getSession();
      setState((current) => {
        if (current.kind !== "signed-in" && current.kind !== "checking") return current;
        if (current.kind === "signed-in" && stored?.token === current.session.token) return current;
        return stored === null ? { kind: "signed-out", notice: null } : { kind: "checking" };
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // The admin console's code, spent once (lib/handoff.ts): the desk, or the expired-link line.
  useEffect(() => {
    if (state.kind !== "handoff") return;
    const exchange = pendingHandoff();
    if (exchange === null) {
      setState(fromStorage());
      return;
    }
    let active = true;
    void exchange.then((outcome) => {
      if (!active) return;
      markHandoffShown();
      setState(afterHandoff(outcome));
    });
    return () => {
      active = false;
    };
  }, [state.kind]);

  // "Does the API still report this session?": at boot, after a sign-in, after "Try again", and
  // after another tab changed the stored session.
  useEffect(() => {
    if (state.kind !== "checking") return;
    const session = getSession();
    if (session === null) {
      setState({ kind: "signed-out", notice: null });
      return;
    }
    const controller = new AbortController();
    api.me({ signal: controller.signal, silent: true }).then(
      (me) => {
        // A newer check (another tab changed the stored session meanwhile) has taken over.
        if (controller.signal.aborted) return;
        setState({ kind: "signed-in", session, me });
      },
      (err: unknown) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        if (err instanceof ApiError && err.status === 401) {
          // Another tab stored a newer session meanwhile: that one is checked, and kept.
          const stored = getSession();
          if (stored !== null && stored.token !== session.token) {
            setState({ kind: "checking" });
            return;
          }
          clearSession();
          setState({ kind: "signed-out", notice: endedNotice(session.kind) });
          return;
        }
        // No answer: keep the token, say so, and let the operator retry.
        setState({
          kind: "signed-out",
          notice: {
            text: "The Maker portal did not answer, so your session could not be confirmed.",
            tone: "grey",
            retry: () => setState({ kind: "checking" }),
          },
        });
      },
    );
    return () => controller.abort();
    // Every new "checking" state checks again, even from "checking".
  }, [state]);

  // Back to the sign-in card the moment the session expires, without waiting for a call.
  const expiresAt = state.kind === "signed-in" ? state.session.expiresAt : null;
  const token = state.kind === "signed-in" ? state.session.token : null;
  useEffect(() => {
    if (expiresAt === null) return;
    const ms = Math.min(Math.max(0, Date.parse(expiresAt) - Date.now()), MAX_TIMEOUT_MS);
    // This session only: a newer one another tab stored is this tab's to check, not to end.
    const timer = setTimeout(() => {
      if (getSession()?.token === token) endSession("expired");
    }, ms);
    return () => clearTimeout(timer);
  }, [expiresAt, token]);

  const signOut = useCallback(() => {
    wallet.disconnect();
    endSession("signed-out");
  }, []);
  const onSignedIn = useCallback(() => setState({ kind: "checking" }), []);

  const signedIn = state.kind === "signed-in" ? state : null;
  const context = useMemo<MakerContextValue | null>(
    // `me` says who the session is now; an older API does not, and the stored kind stands until every API names it.
    () => (signedIn === null ? null : { session: signedIn.session, me: signedIn.me, kind: signedIn.me.kind ?? signedIn.session.kind, signOut }),
    [signedIn, signOut],
  );

  switch (state.kind) {
    case "handoff":
      return <HandoffScreen />;
    case "checking":
      return (
        <div className="boot-screen" role="status" aria-label="Checking your session">
          <span className="spinner" />
        </div>
      );
    case "signed-out":
      return <SignInScreen notice={state.notice} onSignedIn={onSignedIn} />;
    case "signed-in":
      return (
        <MakerContext.Provider value={context}>
          <NetworkProvider chains={state.me.chains}>{children}</NetworkProvider>
        </MakerContext.Provider>
      );
  }
}
